/**
 * Spec Phase 4 Slice 4E — GlossaryDock.
 * Phase 9 Slice 9D — wired to the live glossary feed.
 *
 * Reads /api/matter/{m}/spec/glossary (Stage 1 Technology Basics).
 * Each row is HTML5-draggable with the custom MIME
 * 'application/x-spec-glossary-term'. ParagraphBlock listens for
 * that MIME and inserts the term as plain text at the drop point
 * (NOT a numeral chip — these are definitions, not reference signs).
 *
 * Read-only in Spec — glossary edits happen at Stage 1.
 */
import React, { useEffect } from 'react';
import { BookOpen } from 'lucide-react';
import { useSpecStore } from '@/store/specStore';
import Dock from './Dock';

export const GLOSSARY_DRAG_MIME = 'application/x-spec-glossary-term';

export default function GlossaryDock(): JSX.Element {
  const entries = useSpecStore((s) => s.glossaryEntries);
  const status = useSpecStore((s) => s.glossaryStatus);
  const loadGlossary = useSpecStore((s) => s.loadGlossary);

  // Lazy-load on mount. Idempotent on cache hit.
  useEffect(() => {
    void loadGlossary();
  }, [loadGlossary]);

  return (
    <Dock
      dockKey="glossary"
      icon={<BookOpen className="h-3.5 w-3.5 text-zinc-500" aria-hidden />}
      title="Glossary & defs"
      pillCount={entries.length}
      meta={
        entries.length === 0
          ? (status === 'loading'
              ? 'Loading…'
              : status === 'error'
                ? 'Failed to load glossary.'
                : 'Stage 1 Technology Basics has no glossary terms yet.')
          : 'Drag a term onto a paragraph to insert it inline.'
      }
      testId="spec-dock-glossary"
    >
      {entries.length === 0 ? null : (
        <ul className="space-y-1">
          {entries.map((g) => (
            <li
              key={g.term}
              className="cursor-grab rounded-sm bg-white px-2 py-1.5 hover:bg-zinc-50 focus:outline focus:outline-2 focus:outline-blue-400"
              data-testid={`spec-glossary-row-${g.term}`}
              draggable
              // Phase 12G — a11y. aria-grabbed flips during the
              // browser's drag op so screen-reader users hear the
              // start/end of the drag. tabIndex=0 + Enter / Space
              // keyboard alternative inserts the term at the active
              // paragraph cursor (handled by the existing
              // PaperEditor drop listener which also accepts the
              // 'spec.glossary.insert' bus event).
              tabIndex={0}
              role="button"
              aria-grabbed={false}
              aria-label={`Glossary term ${g.term}: drag onto a paragraph or press Enter to insert at cursor`}
              onDragStart={(e) => {
                e.currentTarget.setAttribute('aria-grabbed', 'true');
                e.dataTransfer.setData(GLOSSARY_DRAG_MIME, g.term);
                e.dataTransfer.setData('text/plain', g.term);
                e.dataTransfer.effectAllowed = 'copy';
              }}
              onDragEnd={(e) => {
                e.currentTarget.setAttribute('aria-grabbed', 'false');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  // Defer to the existing event bus the PaperEditor
                  // listens on. The drop handler accepts this
                  // synthetic insertion path identically to the
                  // drag-and-drop path.
                  window.dispatchEvent(new CustomEvent(
                    'spec.glossary.insert',
                    { detail: { term: g.term } },
                  ));
                }
              }}
            >
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-[10px] font-medium text-zinc-700">
                  {g.term}
                </span>
                <span className="text-[10px] uppercase tracking-wide text-zinc-400">
                  {g.source_section}
                </span>
              </div>
              <p className="mt-0.5 text-[11px] leading-relaxed text-zinc-600 line-clamp-2">
                {g.definition}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Dock>
  );
}
